import { Injectable } from '@angular/core';
import { Observable, concat, defer, of } from 'rxjs';
import { last, map } from 'rxjs/operators';

/**
 * A unit of work that knows whether it holds unsaved changes and how to persist them.
 * Implemented by page components for each of their forms (e.g. header, line items).
 */
export interface PendingSaveTask {
  /** True when the underlying form differs from the last persisted backend state. */
  isDirty(): boolean;
  /** Persists the pending changes. Must emit on success and error on failure. */
  save(): Observable<unknown>;
}

/**
 * Runs the "save pending changes first" step that must precede user actions such as
 * Reject, Place Order, Refresh or navigating away.
 *
 * Only dirty tasks trigger a request, and tasks run in the order supplied so callers
 * can express backend ordering constraints (e.g. line items before header).
 */
@Injectable({ providedIn: 'root' })
export class PreActionSaveService {
  /**
   * Saves every dirty task sequentially and completes once all of them succeed.
   * Completes immediately when nothing is dirty, and errors as soon as a save fails
   * so the caller can skip the requested action.
   */
  save(tasks: readonly PendingSaveTask[]): Observable<void> {
    const pendingSaves = tasks
      .filter((task) => task.isDirty())
      .map((task) => defer(() => task.save()));

    if (pendingSaves.length === 0) {
      return of(undefined);
    }

    return concat(...pendingSaves).pipe(
      last(),
      map(() => undefined)
    );
  }
}
